import { PiggyBank } from 'lucide-react';
import { Checkbox } from '../../components/ui/checkbox';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';

const assetOptions = [
  { key: 'property', label: 'A Home/Property' },
  { key: 'vehicles', label: 'Vehicles' },
  { key: 'boat', label: 'A Boat' },
  { key: 'other', label: 'Other Assets' },
];

const liabilityOptions = [
  { key: 'home_loan', label: 'Home Loan' },
  { key: 'vehicle_loan', label: 'Vehicle Loan' },
  { key: 'boat_loan', label: 'Boat Loan' },
  { key: 'personal_loan', label: 'Personal Loan' },
  { key: 'hire_purchase', label: 'Hire Purchases' },
  { key: 'credit_cards', label: 'Credit Cards' },
  { key: 'other_debts', label: 'Other Debts' },
];

const StepAssetsDebts = ({
  formData,
  handleAssetChange,
  handleLiabilityChange,
  handleNoOtherDebtsChange,
}) => {
  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <div className="w-14 h-14 bg-[#00D47E]/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <PiggyBank className="w-7 h-7 text-[#00D47E]" />
        </div>
        <h2 className="font-outfit text-2xl md:text-3xl font-bold text-black mb-2">
          Assets & Debts
        </h2>
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <Label className="text-gray-700 font-semibold">Do you own any of the following?</Label>
        </div>
        <p className="text-xs text-gray-500 mb-3">You can skip this if in a hurry, but it does help action applications faster</p>

        <div className="space-y-2">
          {assetOptions.map(({ key, label }) => (
            <div key={key} className="flex items-center gap-3 p-3 rounded-lg border border-gray-200">
              <Checkbox
                checked={formData.assets[key].selected}
                onCheckedChange={(checked) => handleAssetChange(key, 'selected', checked)}
              />
              <span className="flex-1 text-sm">{label}</span>
              {formData.assets[key].selected && (
                <div className="relative w-32">
                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
                  <Input
                    type="number"
                    value={formData.assets[key].value}
                    onChange={(e) => handleAssetChange(key, 'value', e.target.value)}
                    className="pl-6 h-8 text-sm"
                    placeholder="Value"
                    data-testid={`asset-value-${key}`}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="pt-4 border-t border-gray-100">
        <Label className="text-gray-700 font-semibold mb-3 block">Do you owe money for any of the following?</Label>

        <div className="space-y-2">
          {liabilityOptions.map(({ key, label }) => (
            <div key={key} className="flex items-center gap-3 p-3 rounded-lg border border-gray-200">
              <Checkbox
                checked={formData.liabilities[key].selected}
                onCheckedChange={(checked) => handleLiabilityChange(key, 'selected', checked)}
                data-testid={`liability-${key}`}
              />
              <span className="flex-1 text-sm">{label}</span>
              {formData.liabilities[key].selected && (
                <div className="relative w-32">
                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
                  <Input
                    type="number"
                    value={formData.liabilities[key].value}
                    onChange={(e) => handleLiabilityChange(key, 'value', e.target.value)}
                    className="pl-6 h-8 text-sm"
                    placeholder="Owing"
                    data-testid={`liability-value-${key}`}
                  />
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Debts drive the DTI part of the score, and an unanswered checklist reads as zero —
            which would score more generously than the applicant's real position. So "none" has to
            be said, not just left blank. */}
        <div className="mt-3 flex items-center gap-3 p-3 rounded-lg border border-gray-200 bg-gray-50">
          <Checkbox
            id="no_other_debts"
            checked={formData.no_other_debts}
            onCheckedChange={(checked) => handleNoOtherDebtsChange(checked === true)}
            data-testid="no-other-debts-checkbox"
          />
          <Label htmlFor="no_other_debts" className="text-sm text-gray-700 font-normal cursor-pointer">
            I don&apos;t have any of these debts
          </Label>
        </div>
      </div>
    </div>
  );
};

export default StepAssetsDebts;
